import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, ExternalLink, Plus, RotateCcw, Truck, X } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import { api } from '../../services/apiService';
import { journeyApi, type ManualCompletions } from '../../services/journeyApi';
import { hardwareApi, type DeviceUnitRow, type DeviceModelRow } from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import { DeviceThumb, unitLabel } from './stageShell';
import DestinationMark from './DestinationMark';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * THE LAST STRETCH OF THE ORDER — receipts, then boxes out of the door.
 *
 * Test and refund transactions are completed BY uploading a photo of the
 * printed receipt; with both photos in, receipt printing completes on its own.
 * Shipped needs a tracking number on every box before it can be marked.
 *
 * Photos go through the shared uploads service into the tenant's own folder,
 * `{tenantId}/admin/orders/{orderId}/receipts/`, with a random file name —
 * that bucket is readable by link, so the link must not be guessable.
 */

const when = (ts: number) =>
  new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const randomId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

/* ── RECEIPT ─────────────────────────────────────────────────────────────── */

export const ReceiptStagePanel: React.FC<{
  stageId: 'test_transaction' | 'refund_transaction';
  what: string;
  tenantId: string;
  orderId: string;
  journeyId: string | null;
  completion?: ManualCompletions[string];
  onRecorded: (manual: ManualCompletions) => void;
}> = ({ stageId, what, tenantId, orderId, journeyId, completion, onRecorded }) => {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const photo = completion?.attachment;

  // A short-lived link for the thumbnail — the key is stored, never a URL.
  useEffect(() => {
    let live = true;
    setPreview(null);
    if (!photo?.key) return;
    api.uploads.getPresignedUrl(tenantId, photo.key)
      .then((r) => { if (live) setPreview(r.presignedUrl); })
      .catch(() => { /* thumbnail stays a placeholder; the record is still there */ });
    return () => { live = false; };
  }, [tenantId, photo?.key]);

  const upload = async (file: File) => {
    if (!journeyId) { toastService.error('The journey record is unavailable, so this cannot be saved yet.'); return; }
    if (!file.type.startsWith('image/')) { toastService.error('Choose a photo of the receipt (JPG, PNG or HEIC).'); return; }
    if (file.size > 10 * 1024 * 1024) { toastService.error('That photo is over 10 MB — take it again at a lower resolution.'); return; }
    setBusy(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const kind = stageId === 'test_transaction' ? 'test' : 'refund';
      const signed = await api.uploads.sign(tenantId, `${kind}-${randomId()}.${ext}`, file.type, `admin/orders/${orderId}/receipts`);
      const put = await fetch(signed.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`The upload was refused (${put.status}).`);
      const res = await journeyApi.complete(journeyId, stageId, undefined, {
        key: signed.key, name: file.name.slice(0, 200), contentType: file.type,
      });
      onRecorded(res.manual);
      toastService.success(`${what} receipt saved.`);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not save the receipt photo.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const undo = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const res = await journeyApi.undo(journeyId, stageId);
      onRecorded(res.manual);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not undo.');
    } finally {
      setBusy(false);
    }
  };

  const picker = (
    <input
      ref={input}
      id={`receipt-${stageId}`}
      type="file"
      accept="image/*"
      capture="environment"
      className="hidden"
      onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
    />
  );

  if (!photo) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] border-dashed border-stone-300 bg-stone-50 p-4">
        {picker}
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[10px] bg-[#FFF1F3]">
          <Camera size={20} className="text-[#DC143C]" />
        </span>
        <div className="min-w-[180px] flex-1">
          <p className="text-[13px] font-semibold text-stone-800">Upload the {what.toLowerCase()} receipt</p>
          <p className="text-[12px] text-stone-400">Take a photo or choose one · JPG, PNG or HEIC, up to 10 MB</p>
          {completion && (
            <p className="mt-1 text-[11.5px] text-amber-700">Marked done by {completion.name} without a photo — upload one to count toward receipt printing.</p>
          )}
        </div>
        <button
          onClick={() => input.current?.click()}
          disabled={busy || !journeyId}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-[#DC143C] px-4 text-[13px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40"
        >
          {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <Camera size={14} />}
          {busy ? 'Uploading…' : 'Upload photo'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-4">
      {picker}
      <button
        onClick={() => preview && window.open(preview, '_blank', 'noopener')}
        className="h-[132px] w-[104px] shrink-0 overflow-hidden rounded-md border border-stone-200 bg-stone-100"
        aria-label={`View the ${what.toLowerCase()} receipt full size`}
      >
        {preview
          ? <img src={preview} alt={`${what} receipt`} className="h-full w-full object-cover" />
          : <span className="grid h-full place-items-center"><RubyGemMark size={14} drawing /></span>}
      </button>
      <div className="min-w-[200px] flex-1 space-y-2">
        <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-[12.5px]">
          <dt className="pt-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-stone-400">Uploaded</dt>
          <dd className="text-stone-700">{completion ? `${completion.name} · ${when(completion.at)}` : '—'}</dd>
          <dt className="pt-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-stone-400">Photo</dt>
          <dd className="break-all text-stone-700">{photo.name || photo.key.split('/').pop()}</dd>
        </dl>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => preview && window.open(preview, '_blank', 'noopener')}
            disabled={!preview}
            className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-4 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
          >View full size</button>
          <button
            onClick={() => input.current?.click()}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-4 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
          >
            {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Camera size={13} />} Replace photo
          </button>
          <button
            onClick={undo}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-semibold text-stone-500 hover:text-[#DC143C] disabled:opacity-40"
          >
            <RotateCcw size={13} /> Undo
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── RECEIPT PRINTING ────────────────────────────────────────────────────── */

export const ReceiptPrintingPanel: React.FC<{
  journeyId: string | null;
  manual: ManualCompletions;
  done: boolean;
  onRecorded: (manual: ManualCompletions) => void;
}> = ({ journeyId, manual, done, onRecorded }) => {
  const [busy, setBusy] = useState(false);
  const chips = [
    { label: 'Test receipt', ok: !!manual.test_transaction?.attachment },
    { label: 'Refund receipt', ok: !!manual.refund_transaction?.attachment },
  ];
  const ticked = manual.receipt_printing;

  const act = async (undo: boolean) => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const res = undo
        ? await journeyApi.undo(journeyId, 'receipt_printing')
        : await journeyApi.complete(journeyId, 'receipt_printing');
      onRecorded(res.manual);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <span key={c.label} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] ${
            c.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-stone-200 bg-stone-50 text-stone-600'}`}>
            <span className={`grid h-3.5 w-3.5 place-items-center rounded-full ${c.ok ? 'bg-emerald-500' : 'bg-stone-300'}`}>
              <Check size={9} className="text-white" strokeWidth={3} />
            </span>
            {c.label}
          </span>
        ))}
      </div>
      {ticked
        ? (
          <button onClick={() => act(true)} disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-200 px-3 text-[12px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50">
            {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : <RotateCcw size={12} />} Undo manual tick
          </button>
        )
        : !done && (
          <button onClick={() => act(false)} disabled={busy || !journeyId}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-4 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50">
            {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : <Check size={12} strokeWidth={3} />} Mark verified anyway
          </button>
        )}
    </div>
  );
};

/* ── SHIPPED ─────────────────────────────────────────────────────────────── */

const CARRIERS = ['UPS', 'FedEx', 'USPS', 'DHL', 'OnTrac'];

/**
 * WHERE A TRACKING NUMBER CAN BE LOOKED UP.
 *
 * Only for the carriers we offer by name, and only ever opened in a new tab —
 * a typed carrier we do not recognise gets no link rather than a guessed URL
 * that 404s. Nothing is stored: this is the same number already on the box,
 * put where somebody can press it.
 */
const TRACK_URL: Record<string, (n: string) => string> = {
  UPS: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  FedEx: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  USPS: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  DHL: (n) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(n)}`,
  OnTrac: (n) => `https://www.ontrac.com/tracking/?number=${encodeURIComponent(n)}`,
};

const trackingLink = (carrier?: string, number?: string): string | null => {
  const n = (number || '').trim();
  const make = carrier ? TRACK_URL[carrier] : undefined;
  return n && make ? make(n) : null;
};

type PackedBox = { boxNo: number; carrier?: string; trackingNumber?: string; unitIds: string[]; bulkLines?: { modelId: string; qty: number }[] };

export const ShippedStagePanel: React.FC<{
  orderId: string;
  journeyId: string | null;
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  shippedMark?: ManualCompletions[string];
  onRecorded: (manual: ManualCompletions) => void;
  /**
   * Patch ONE device in place. Shipping must not reload the order: a reload
   * unmounts every stage and throws the operator back up the page.
   */
  onUnitChanged: (unit: DeviceUnitRow) => void;
}> = ({ orderId, journeyId, units, models, shippedMark, onRecorded, onUnitChanged }) => {
  const [boxes, setBoxes] = useState<PackedBox[] | null>(null);
  const [drafts, setDrafts] = useState<Record<number, { carrier: string; trackingNumber: string }>>({});
  const [savingBox, setSavingBox] = useState<number | null>(null);
  const [shipping, setShipping] = useState(false);
  const dataVersion = useDataVersion();
  /** What the server said each box held at the last read — tells a typed draft from a stale one. */
  const serverDrafts = useRef<Record<number, { carrier: string; trackingNumber: string }>>({});

  const load = useCallback(async () => {
    try {
      const res = await hardwareApi.getPacking(orderId);
      const list = [...(res.boxes || [])].sort((a, b) => a.boxNo - b.boxNo);
      setBoxes(list);
      const fresh = Object.fromEntries(list.map((b) => [b.boxNo, { carrier: b.carrier || '', trackingNumber: b.trackingNumber || '' }]));
      const was = serverDrafts.current;
      serverDrafts.current = fresh;
      // Keep anything the operator has typed but not saved; everything else takes the server's value.
      setDrafts((cur) => Object.fromEntries(list.map((b) => {
        const d = cur[b.boxNo];
        const w = was[b.boxNo];
        const edited = d && (!w || d.carrier !== w.carrier || d.trackingNumber !== w.trackingNumber);
        return [b.boxNo, edited ? d : fresh[b.boxNo]];
      })));
    } catch (e: any) {
      toastService.error(e?.message || 'Could not read the boxes.');
      setBoxes((cur) => cur ?? []);
    }
  }, [orderId, dataVersion]); // dataVersion: re-read after any save on the page

  useEffect(() => { load(); }, [load]);

  const modelName = (id: string) => models.find((m) => m.modelId === id)?.modelName || id;
  const modelOf = (id?: string) => models.find((m) => m.modelId === id);

  /**
   * WHAT IS IN THE BOX, AS THE DEVICES THEMSELVES.
   *
   * This was one run-on string — "TC-TOUCH-K10 · RTA-000041 + Dejavoo P17 ·
   * RTA-000027 + 1 × Cash Drawer" — which is the packing list written as prose
   * and is read by nobody holding a carton. Same contents, same order, drawn
   * as the machines they are, with the picture and the tracking id that is on
   * the label.
   */
  const contentChips = (b: PackedBox) => {
    const chips = b.unitIds.map((id) => {
      const u = units.find((x) => x.unitId === id);
      return (
        <span
          key={id}
          className="flex items-center gap-2 rounded-[10px] border border-stone-200 bg-white py-1 pl-1 pr-2"
        >
          <DeviceThumb model={modelOf(u?.modelId)} size={30} />
          <span className="min-w-0 leading-tight">
            <span className="block font-mono text-[10.5px] font-bold tracking-[0.06em] text-stone-800">
              {u ? unitLabel(u) : id}
            </span>
            <span className="block text-[11px] text-stone-500">
              {u ? modelName(u.modelId) : 'unknown device'}
            </span>
          </span>
          {u && !['shipped', 'delivered', 'deployed'].includes(u.state) && (
            <button
              onClick={() => unpack(u)}
              disabled={moving === u.unitId}
              aria-label={`Take ${unitLabel(u)} out of box ${b.boxNo}`}
              title="Take out of this box"
              className="ml-1 text-stone-400 hover:text-[#DC143C] disabled:opacity-40"
            >
              {moving === u.unitId ? <RubyGemMark size={12} drawing color="currentColor" /> : <X size={13} />}
            </button>
          )}
        </span>
      );
    });

    /* Counted models have no unit row to draw, so they stay a line. */
    const bulk = (b.bulkLines || []).map((l) => (
      <span
        key={`bulk-${l.modelId}`}
        className="flex items-center gap-2 rounded-[10px] border border-stone-200 bg-white py-1 pl-1 pr-2.5"
      >
        <DeviceThumb model={modelOf(l.modelId)} size={30} />
        <span className="leading-tight">
          <span className="block text-[11px] font-semibold text-stone-800">{l.qty} ×</span>
          <span className="block text-[11px] text-stone-500">{modelName(l.modelId)}</span>
        </span>
      </span>
    ));

    return [...chips, ...bulk];
  };

  const saveBox = async (boxNo: number) => {
    const d = drafts[boxNo];
    const b = boxes?.find((x) => x.boxNo === boxNo);
    if (!d || !b) return;
    if (d.carrier === (b.carrier || '') && d.trackingNumber.trim() === (b.trackingNumber || '')) return;
    setSavingBox(boxNo);
    try {
      await hardwareApi.upsertBox(orderId, {
        boxNo, carrier: d.carrier || undefined, trackingNumber: d.trackingNumber.trim() || undefined,
      });
      setBoxes((cur) => (cur || []).map((x) => x.boxNo === boxNo ? { ...x, carrier: d.carrier, trackingNumber: d.trackingNumber.trim() } : x));

      /*
        A CORRECTION AFTER SHIPPING. Devices already out of the door carry
        their own copy of the tracking, so it is corrected on them too — same
        state, new shipment. The server keeps the original shippedAt and only
        emails on a state CHANGE, so a fix never re-sends the shipping email.
      */
      if (d.carrier && d.trackingNumber.trim()) {
        const out = b.unitIds
          .map((id) => units.find((u) => u.unitId === id))
          .filter((u): u is DeviceUnitRow => !!u && ['shipped', 'delivered', 'deployed'].includes(u.state));
        let fixed = 0;
        for (const u of out) {
          try {
            const res = await hardwareApi.setUnitStatus(u.unitId, {
              state: u.state,
              shipment: { carrier: d.carrier, trackingNumber: d.trackingNumber.trim() },
            });
            if (res?.unit) onUnitChanged(res.unit);
            fixed += 1;
          } catch (e: any) {
            toastService.error(`${u.trackingId || u.unitId}: ${e?.message || 'tracking not updated'}`);
          }
        }
        if (fixed > 0) toastService.success(`Box ${boxNo} tracking corrected on ${fixed} shipped device${fixed === 1 ? '' : 's'}.`);
      }
    } catch (e: any) {
      toastService.error(e?.message || `Could not save box ${boxNo}.`);
    } finally {
      setSavingBox(null);
    }
  };

  const addBox = async () => {
    try {
      await hardwareApi.upsertBox(orderId, {});
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not add a box.');
    }
  };

  /**
   * PUT A DEVICE IN A BOX, FROM HERE.
   *
   * The same `packUnitIntoBox` the packing view uses — so the server's gates
   * (provisioning incomplete, unsigned agreement, cancelled order) still
   * refuse in exactly the same words. Nothing new is allowed by doing it on
   * this screen; it is only that the person deciding which box a machine goes
   * in is the person standing at the boxes with the labels.
   */
  const [moving, setMoving] = useState<string | null>(null);
  const [adding, setAdding] = useState<number | null>(null);

  const packInto = async (boxNo: number, unitId: string) => {
    setMoving(unitId);
    try {
      const res = await hardwareApi.packUnitIntoBox(orderId, boxNo, unitId);
      if (res?.unit) onUnitChanged(res.unit);
      await load();
      setAdding(null);
    } catch (e: any) {
      /* The service's own sentence — it names the gate that refused. */
      toastService.error(e?.serverMessage || e?.message || 'Could not pack that device.');
    } finally {
      setMoving(null);
    }
  };

  /**
   * TAKE ONE OUT.
   *
   * There is no unpack endpoint: a unit's box is derived from its state, and
   * packing it into a DIFFERENT box is the move the service supports. So
   * "take out" is only offered where there is somewhere to put it — and with
   * one box it says so rather than offering an action that cannot be carried
   * out.
   */
  const unpack = async (u: DeviceUnitRow) => {
    const others = (boxes || []).filter((b) => !b.unitIds.includes(u.unitId));
    if (others.length === 0) {
      toastService.info('Add another box first — a packed device moves between boxes rather than out of all of them.');
      return;
    }
    if (others.length === 1) { await packInto(others[0]!.boxNo, u.unitId); return; }
    /* More than one candidate is a choice, not a guess: the chip's own picker
       handles it, so this only fires when the answer is unambiguous. */
    setAdding(others[0]!.boxNo);
    toastService.info(`Pick the box to move ${u.trackingId || u.unitId} into.`);
  };

  const list = boxes || [];
  const untracked = list.filter((b) => !(drafts[b.boxNo]?.carrier && drafts[b.boxNo]?.trackingNumber.trim()));
  /*
    EVERY DEVICE IN A BOX. Shipping moves the devices inside each box, so a
    device in no box would be marked shipped on the rail while it is still on
    the shelf — exactly what happened with two empty tracked boxes on dev.
  */
  const boxed = new Set(list.flatMap((b) => b.unitIds));
  // Quantity-tracked models (a cash drawer) are packed as counted lines, not units.
  const isCounted = (modelId: string) => (models.find((m) => m.modelId === modelId) as any)?.trackingMode === 'quantity';
  const unboxed = units.filter((u) =>
    !boxed.has(u.unitId) && !isCounted(u.modelId) && !['shipped', 'delivered', 'deployed'].includes(u.state));
  const empty = list.filter((b) => b.unitIds.length === 0 && !(b.bulkLines || []).length);
  const blocker = list.length === 0
    ? 'Pack the order into at least one box first.'
    : unboxed.length > 0
      ? `${unboxed.length} device${unboxed.length === 1 ? ' is' : 's are'} not packed into a box yet (${unboxed.map((u) => u.trackingId || u.serialNumber || u.unitId).join(', ')}).`
      : empty.length > 0
        ? `${empty.map((b) => `Box ${b.boxNo}`).join(', ')} ${empty.length === 1 ? 'is' : 'are'} empty — pack it or remove it.`
        : untracked.length > 0
          ? `${untracked.map((b) => `Box ${b.boxNo}`).join(', ')} ${untracked.length === 1 ? 'needs' : 'need'} a carrier and tracking number.`
          : null;

  const markShipped = async () => {
    if (blocker || !journeyId) return;
    setShipping(true);
    const failures: string[] = [];
    try {
      for (const b of list) await saveBox(b.boxNo);
      for (const b of list) {
        const d = drafts[b.boxNo];
        for (const unitId of b.unitIds) {
          const u = units.find((x) => x.unitId === unitId);
          if (u && ['shipped', 'delivered', 'deployed'].includes(u.state)) continue;
          try {
            const res = await hardwareApi.setUnitStatus(unitId, {
              state: 'shipped',
              shipment: { carrier: d.carrier, trackingNumber: d.trackingNumber.trim() },
            });
            if (res?.unit) onUnitChanged(res.unit);
          } catch (e: any) {
            failures.push(`${u?.trackingId || unitId}: ${e?.message || 'refused'}`);
          }
        }
      }
      if (failures.length) {
        // A partial ship must never read as shipped.
        failures.forEach((f) => toastService.error(f));
        return;
      }
      const res = await journeyApi.complete(journeyId, 'shipped');
      onRecorded(res.manual);
      toastService.success('Shipped. Each device carries its own box\'s tracking, and the merchant has been emailed.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not mark shipped.');
    } finally {
      setShipping(false);
    }
  };

  if (boxes === null) {
    return <RubyLoader variant="inline" label="reading boxes…" />;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2.5">
        {list.map((b) => {
          const d = drafts[b.boxNo] || { carrier: '', trackingNumber: '' };
          const missing = !(d.carrier && d.trackingNumber.trim());
          return (
            <div key={b.boxNo} className={`grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-3 rounded-xl border p-3 ${
              missing ? 'border-amber-300 bg-amber-50' : 'border-stone-200 bg-white'}`}>
              <span className={`grid h-11 w-11 place-items-center rounded-[10px] font-mono text-[15px] font-bold text-white ${
                missing ? 'bg-amber-600' : 'bg-stone-900'}`}>{b.boxNo}</span>
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-stone-900">Box {b.boxNo}</p>
                <p className="text-[12px] text-stone-500">
                  {b.unitIds.length + (b.bulkLines || []).length === 0
                    ? 'Empty'
                    : `${b.unitIds.length} device${b.unitIds.length === 1 ? '' : 's'}`}
                </p>
              </div>

              {/* WHAT IS IN IT — the machines, with the labels that are on them. */}
              <div className="col-span-2 flex flex-wrap items-center gap-2">
                {contentChips(b)}
                {unboxed.length > 0 && (
                  adding === b.boxNo ? (
                    <span className="min-w-[220px]">
                      <SearchableDropdown
                        options={unboxed.map((u) => ({
                          value: u.unitId,
                          label: `${modelName(u.modelId)} · ${unitLabel(u)}`,
                        }))}
                        value={null}
                        onChange={(v) => { if (v) packInto(b.boxNo, v); }}
                        placeholder="Which device…"
                        variant="light"
                      />
                    </span>
                  ) : (
                    <button
                      onClick={() => setAdding(b.boxNo)}
                      className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-stone-300 px-3 text-[11.5px] font-semibold text-stone-500 hover:border-[#DC143C] hover:text-[#DC143C]"
                    >
                      <Plus size={12} /> Add a device
                    </button>
                  )
                )}
              </div>
              <div className="col-span-2 grid gap-2 sm:grid-cols-[170px_1fr]">
                <div>
                  <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-stone-400">Carrier</label>
                  <SearchableDropdown
                    options={CARRIERS.map((c) => ({ value: c, label: c }))}
                    value={d.carrier || null}
                    onChange={(v) => {
                      setDrafts((cur) => ({ ...cur, [b.boxNo]: { ...d, carrier: v || '' } }));
                    }}
                    onCreateNew={(name) => setDrafts((cur) => ({ ...cur, [b.boxNo]: { ...d, carrier: name } }))}
                    createLabel="Use carrier"
                    placeholder="Choose carrier"
                    variant="light"
                  />
                </div>
                <div>
                  <label htmlFor={`tracking-${b.boxNo}`} className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-stone-400">Tracking number</label>
                  <input
                    id={`tracking-${b.boxNo}`}
                    value={d.trackingNumber}
                    onChange={(e) => setDrafts((cur) => ({ ...cur, [b.boxNo]: { ...d, trackingNumber: e.target.value } }))}
                    onBlur={() => saveBox(b.boxNo)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveBox(b.boxNo); } }}
                    placeholder="Scan or type the label…"
                    className="min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 font-mono text-[13px] tracking-wide text-stone-800 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
                  />
                </div>
              </div>
              {savingBox === b.boxNo && (
                <p className="col-span-2"><RubyLoader variant="inline" size={14} label="Saving…" /></p>
              )}

              {/* TRACK IT. The number is already here; this only puts it where
                  it can be pressed. Unknown carrier, no link — never a guessed
                  URL. */}
              {d.carrier && d.trackingNumber.trim() && (
                <div className="col-span-2 flex flex-wrap items-center gap-2.5 border-t border-dashed border-stone-200 pt-2.5">
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-bold text-emerald-700">
                    {d.carrier}
                  </span>
                  <span className="font-mono text-[12px] font-semibold tracking-[0.06em] text-stone-800">
                    {d.trackingNumber.trim()}
                  </span>
                  {trackingLink(d.carrier, d.trackingNumber) && (
                    <a
                      href={trackingLink(d.carrier, d.trackingNumber)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[11.5px] font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C]"
                    >
                      Track this box <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button
          onClick={addBox}
          className="inline-flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-stone-300 text-[13px] font-semibold text-stone-600 hover:border-[#DC143C] hover:text-[#DC143C]"
        >
          <Plus size={14} /> Add box
        </button>
      </div>

      {shippedMark
        ? (
          <div className="space-y-0.5">
            <p className="text-[12.5px] text-emerald-700">Marked shipped by {shippedMark.name} · {when(shippedMark.at)}</p>
            <p className="text-[12px] text-stone-500">
              Wrong number? Edit it above — the correction is saved to the box and to every device in it, without re-sending the email.
            </p>
          </div>
        )
        : (
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
            <button
              onClick={markShipped}
              disabled={!!blocker || shipping || !journeyId}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-[#DC143C] px-4 text-[13px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40"
            >
              {shipping ? <RubyGemMark size={14} drawing color="currentColor" /> : <Truck size={14} />}
              {shipping ? 'Shipping…' : 'Mark shipped'}
            </button>
            {blocker && <span className="text-[12.5px] text-amber-700">{blocker}</span>}
          </div>
        )}
      <p className="text-[12px] text-stone-400">
        Marking shipped moves every device to Shipped with its own box&apos;s tracking, and sends the merchant the shipping email.
      </p>

      {/* The rail stops here with where the boxes are going. */}
      <DestinationMark />
    </div>
  );
};
